import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  ImageList,
  ImageListItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  CollectionsOutlined,
  Download,
  ChevronLeft,
  ChevronRight,
  ChildCare,
} from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getChildren as getChildrenApi } from "apis";
import { IChild } from "types";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Axios from "axios";
import { stringAvatar } from "utils";
import { toAssetUrl } from "utils/apiConfig";

const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [images, setImages] = useState<any[]>([]);
  const [children, setChildren] = useState<IChild[]>([]);
  const [selectedChild, setSelectedChild] = useState<IChild | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    getChildrenApi()
      .then((data) => {
        const list = data || [];
        setChildren(list);
        if (list[0]) {
          setSelectedChild(list[0]);
          fetchGallery(list[0].id);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchGallery = async (id: any) => {
    setLoading(true);
    try {
      const { data } = await Axios.post("/api/get_gallery_image", { id });
      setImages(data || []);
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const allImages = useMemo(() => {
    const all = images.flatMap((item) => item.images || []);
    return all.filter((url: string, i: number) => all.indexOf(url) === i);
  }, [images]);

  const cols = isMobile ? 2 : isTablet ? 3 : 4;

  const handleSelectChild = (child: IChild) => {
    setSelectedChild(child);
    fetchGallery(child.id);
  };

  const handleDownload = async () => {
    if (!selectedChild || allImages.length === 0) return;
    setDownloading(true);
    try {
      const zip = new JSZip();
      const promises = allImages.map(async (url: string) => {
        try {
          const { data: blob } = await Axios.get<Blob>(url, {
            responseType: "blob",
          });
          const fileName = url.substring(url.lastIndexOf("/") + 1);
          zip.file(fileName, blob);
        } catch {}
      });
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(
        content,
        `${selectedChild.firstName}-${selectedChild.lastName}-gallery.zip`,
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleLightboxPrev = () => {
    setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
  };

  const handleLightboxNext = () => {
    setLightboxIndex((prev) =>
      prev !== null && prev < allImages.length - 1 ? prev + 1 : prev,
    );
  };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handleLightboxPrev();
      else if (e.key === "ArrowRight") handleLightboxNext();
      else if (e.key === "Escape") setLightboxIndex(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, allImages.length]);

  return (
    <>
      {/* Toolbar */}
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
          py: 2,
        }}
      >
        {/* Child selector chips */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flex={1}
            minWidth={0}
            flexWrap="wrap"
          >
            <ChildCare sx={{ color: "text.secondary", fontSize: 20 }} />
            {children.map((child) => (
              <Chip
                key={child.id}
                avatar={
                  child.photo ? (
                    <Avatar src={toAssetUrl(child.photo as string)} />
                  ) : (
                    <Avatar
                      {...stringAvatar(`${child.firstName} ${child.lastName}`, {
                        width: 24,
                        height: 24,
                        fontSize: 11,
                      })}
                    />
                  )
                }
                label={`${child.firstName} ${child.lastName}`}
                onClick={() => handleSelectChild(child)}
                variant={selectedChild?.id === child.id ? "filled" : "outlined"}
                color={selectedChild?.id === child.id ? "primary" : "default"}
                sx={{
                  fontWeight: selectedChild?.id === child.id ? 600 : 400,
                  transition: "all 0.2s",
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>

          {/* Download button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={
              downloading ? <CircularProgress size={16} /> : <Download />
            }
            onClick={handleDownload}
            disabled={!selectedChild || allImages.length === 0 || downloading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {downloading ? t("timeline.downloading") : t("gallery.download")}
          </Button>
        </Box>

        {/* Photo count */}
        {selectedChild && !loading && (
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <Typography variant="caption" color="text.secondary">
              {allImages.length}{" "}
              {allImages.length === 1
                ? t("gallery.photo")
                : t("gallery.photos")}
            </Typography>
          </Box>
        )}
      </Toolbar>

      {/* Gallery grid */}
      <Box sx={{ p: isMobile ? 1 : 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : allImages.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={8}
            color="text.secondary"
          >
            <CollectionsOutlined
              sx={{ fontSize: 64, mb: 2, color: "action.disabled" }}
            />
            <Typography variant="h6" gutterBottom>
              {t("gallery.empty")}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t("gallery.empty_hint")}
            </Typography>
          </Box>
        ) : (
          <ImageList cols={cols} gap={isMobile ? 4 : 8} variant="masonry">
            {allImages.map((url: string, idx: number) => (
              <ImageListItem
                key={url}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  "&:hover": {
                    "& img": { transform: "scale(1.05)" },
                    "& .overlay": { opacity: 1 },
                  },
                }}
                onClick={() => setLightboxIndex(idx)}
              >
                <img
                  src={toAssetUrl(url)}
                  alt={`${selectedChild ? `${selectedChild.firstName} ${selectedChild.lastName}` : "Gallery"} — ${idx + 1}`}
                  loading="lazy"
                  style={{
                    display: "block",
                    width: "100%",
                    transition: "transform 0.3s ease",
                  }}
                />
                <Box
                  className="overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.15)",
                    opacity: 0,
                    transition: "opacity 0.3s",
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </Box>

      {/* Lightbox */}
      <Dialog
        open={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "visible",
          },
        }}
        BackdropProps={{
          sx: { bgcolor: "rgba(0,0,0,0.9)" },
        }}
      >
        {lightboxIndex !== null && (
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Close */}
            <IconButton
              onClick={() => setLightboxIndex(null)}
              sx={{
                position: "fixed",
                top: 16,
                right: 16,
                color: "common.white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              <Close />
            </IconButton>

            {/* Prev */}
            {lightboxIndex > 0 && (
              <IconButton
                onClick={handleLightboxPrev}
                sx={{
                  position: "fixed",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "common.white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                <ChevronLeft fontSize="large" />
              </IconButton>
            )}

            {/* Next */}
            {lightboxIndex < allImages.length - 1 && (
              <IconButton
                onClick={handleLightboxNext}
                sx={{
                  position: "fixed",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "common.white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                <ChevronRight fontSize="large" />
              </IconButton>
            )}

            {/* Image */}
            <img
              src={toAssetUrl(allImages[lightboxIndex])}
              alt={`${selectedChild ? `${selectedChild.firstName} ${selectedChild.lastName}` : "Gallery"} — ${lightboxIndex + 1}`}
              style={{
                maxWidth: "85vw",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />

            {/* Counter */}
            <Typography
              sx={{
                position: "fixed",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                color: "rgba(255,255,255,0.7)",
                fontSize: 14,
              }}
            >
              {lightboxIndex + 1} / {allImages.length}
            </Typography>
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default Gallery;
